import React, { useEffect, useState } from "react";
import { api, API, errMsg } from "@finance/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@finance/components/ui/dialog";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Label } from "@finance/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { SearchableSelect } from "@finance/components/SearchableSelect";
import { Checkbox } from "@finance/components/ui/checkbox";
import { UploadSimple, Circle } from "@phosphor-icons/react";

const DOC_TYPES = [
  "Purchase_Order", "Supplier_PFI", "Supplier_Tax_Invoice", "Tax_Receipt", "Delivery_Note",
  "Bank_Statement", "Credit_Note", "Expense_Receipt", "Payroll_Payslip",
];

// Map OCR field keys to transaction fields for confirmation
const TXN_FIELD_KEYS = [
  "client_po_no", "lx_po_no", "currency", "supplier_pfi_no", "supplier_pfi_date",
  "supplier_tax_invoice_no", "supplier_tax_invoice_date", "supplier_efd_qr_code",
  "supplier_efd_time", "supplier_delivery_note_no", "lx_invoice_no", "commercial_invoice_no",
  "bl_awb_no", "freight_charges", "customs_duty",
];

function ConfDot({ score }) {
  const s = score ?? 0;
  const color = s >= 0.8 ? "text-emerald-500" : s >= 0.5 ? "text-amber-500" : "text-red-500";
  return <Circle size={9} weight="fill" className={color} title={`${Math.round(s * 100)}% confidence`} />;
}

export default function UploadDialog({ open, onOpenChange, existingDoc, onDone, presetType, forceCreate }) {
  const [docType, setDocType] = useState("Purchase_Order");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [doc, setDoc] = useState(null);
  const [fields, setFields] = useState({});
  const [conf, setConf] = useState({});
  const [blobUrl, setBlobUrl] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [matchId, setMatchId] = useState("none");
  const [createNew, setCreateNew] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFile(null); setUploading(false);
    if (existingDoc) {
      setDoc(existingDoc);
      setDocType(existingDoc.document_type);
      setFields(flatten(existingDoc.ocr_extracted_fields || {}));
      setConf(existingDoc.ocr_confidence || {});
      setMatchId(existingDoc.transaction_id || "none");
      loadBlob(existingDoc);
    } else {
      setDoc(null); setFields({}); setConf({}); setDocType(presetType || "Purchase_Order"); setBlobUrl(null); setMatchId("none"); setCreateNew(!!forceCreate);
    }
    api.get("/transactions").then((r) => setTransactions(r.data));
    // eslint-disable-next-line
  }, [open, existingDoc]);

  const flatten = (f) => {
    const out = {};
    Object.entries(f || {}).forEach(([k, v]) => {
      if (v !== null && typeof v === "object") out[k] = JSON.stringify(v);
      else out[k] = v;
    });
    return out;
  };

  const loadBlob = async (d) => {
    try {
      const path = d.storage_path;
      const resp = await api.get(`/files/${path}`, { responseType: "blob" });
      setBlobUrl(URL.createObjectURL(resp.data));
    } catch { setBlobUrl(null); }
  };

  const doUpload = async () => {
    if (!file) { toast.error("Select a file"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("document_type", docType);
    fd.append("run_extraction", "true");
    try {
      const { data } = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDoc(data);
      setFields(flatten(data.ocr_extracted_fields || {}));
      setConf(data.ocr_confidence || {});
      loadBlob(data);
      if (forceCreate) setCreateNew(true);
      if (data.ocr_error) toast.warning("OCR could not read this file — enter fields manually.");
      else toast.success("Fields extracted. Review and confirm.");
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setUploading(false); }
  };

  const confirm = async () => {
    if (!doc) return;
    const payload = {
      fields: buildTxnFields(),
      transaction_id: matchId !== "none" ? matchId : null,
      create_transaction: createNew,
    };
    try {
      const { data } = await api.post(`/documents/${doc.id}/confirm`, payload);
      toast.success(data.transaction_id ? "Confirmed & linked to transaction" : "Document confirmed");
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removeDoc = async () => {
    if (!doc) return;
    if (!window.confirm("Delete this document from the inbox?")) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Document deleted");
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const buildTxnFields = () => {
    const out = {};
    TXN_FIELD_KEYS.forEach((k) => { if (fields[k] != null && fields[k] !== "") out[k] = fields[k]; });
    return out;
  };

  const fieldKeys = Object.keys(fields);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl p-0" data-testid="upload-dialog">
        <DialogHeader className="border-b border-gray-200 px-6 py-4">
          <DialogTitle className="font-display">{existingDoc ? "Review Document" : "Upload Document"}</DialogTitle>
        </DialogHeader>

        {!doc ? (
          <div className="space-y-4 p-6">
            <div>
              <Label className="lx-label">Document Type</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="mt-1" data-testid="upload-doctype"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="lx-label">File (image or PDF — OCR reads both; PDFs are converted to images for extraction)</Label>
              <label className="mt-1 flex cursor-pointer flex-col items-center justify-center rounded-sm border-2 border-dashed border-gray-300 p-10 hover:border-[#1B4383]">
                <UploadSimple size={28} className="text-gray-400" />
                <span className="mt-2 text-sm text-gray-600">{file ? file.name : "Click to select a file"}</span>
                <input type="file" className="hidden" data-testid="upload-file-input" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files[0])} />
              </label>
            </div>
            <div className="flex justify-end">
              <Button className="bg-[#1B4383] hover:bg-[#122E5C]" disabled={uploading} data-testid="upload-submit" onClick={doUpload}>
                {uploading ? "Extracting…" : "Upload & Extract"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-0" style={{ maxHeight: "70vh" }}>
            {/* Left: extracted fields */}
            <div className="overflow-y-auto border-r border-gray-200 p-6">
              <div className="lx-label mb-3">Extracted Fields · review low-confidence flags</div>
              <div className="space-y-3">
                {fieldKeys.length === 0 && <div className="text-sm text-gray-400">No fields extracted. Add matching below.</div>}
                {fieldKeys.map((k) => (
                  <div key={k}>
                    <Label className="flex items-center gap-1.5 lx-label">
                      <ConfDot score={conf[k]} /> {k.replace(/_/g, " ")}
                    </Label>
                    <Input
                      data-testid={`ocr-field-${k}`}
                      value={fields[k] ?? ""}
                      onChange={(e) => setFields({ ...fields, [k]: e.target.value })}
                      className={`mt-1 ${(conf[k] ?? 0) < 0.5 ? "border-red-300 bg-red-50/40" : ""}`}
                    />
                  </div>
                ))}
              </div>

              <div className="mt-6 space-y-3 rounded-sm border border-gray-200 bg-gray-50 p-4">
                <div className="lx-label">Link to Transaction</div>
                <SearchableSelect testid="match-transaction" placeholder="Select transaction"
                  value={matchId} onValueChange={(v) => { setMatchId(v); if (v !== "none") setCreateNew(false); }}
                  options={[{ value: "none", label: "— No transaction —" }, ...transactions.map((t) => ({ value: t.id, label: `${t.lx_po_no || t.client_po_no || t.id.slice(0, 8)} · ${t.supplier_name || t.client_name || ""}` }))]} />
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={createNew} data-testid="create-new-txn" onCheckedChange={(v) => { setCreateNew(v); if (v) setMatchId("none"); }} />
                  Create a new transaction from these fields
                </label>
              </div>
            </div>

            {/* Right: viewer */}
            <div className="flex items-center justify-center overflow-hidden bg-gray-100 p-4">
              {blobUrl ? (
                doc.content_type?.includes("pdf")
                  ? <iframe title="doc" src={blobUrl} className="h-full w-full" />
                  : <img src={blobUrl} alt="document" className="max-h-full max-w-full object-contain" />
              ) : (
                <div className="text-sm text-gray-400">Loading preview…</div>
              )}
            </div>
          </div>
        )}

        {doc && (
          <DialogFooter className="border-t border-gray-200 px-6 py-4 sm:justify-between">
            <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" data-testid="delete-doc-btn" onClick={removeDoc}>Delete Document</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="confirm-doc" onClick={confirm}>Confirm &amp; Save</Button>
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
